import classNames from "classnames";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApproveRequest, useDeleteRequest, useRequest } from "../../api/developmentRequests";
import { useUserPicture, useUsers } from "../../api/users";
import { Avatar } from "../../components/Avatar";
import BackButton from "../../components/ui/BackButton";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Page } from "../../components/ui/Page";
import PageLoader from "../../components/ui/PageLoader";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import type { DevelopmentRequestResponse } from "../../types/developmentRequest";
import { formatUserName } from "../../types/user";
import { formatDate } from "../../utils/date";
import styles from "./ViewRequestPage.module.css";

function statusLabel(status: DevelopmentRequestResponse["status"], t: (k: string) => string) {
  const map: Record<DevelopmentRequestResponse["status"], string> = {
    draft: t("requests.statusDraft"),
    new: t("requests.statusNew"),
    in_progress: t("requests.statusInProgress"),
    completed: t("requests.statusCompleted"),
  };
  return map[status];
}

export default function ViewRequestPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: request, isLoading } = useRequest(Number(id) ?? 0);
  useLayoutTitle(
    request ? t("requests.viewRequestPageTitle", { id: request.id, title: request.title }) : t("requests.allRequests"),
  );
  const currentUser = useCurrentUser();
  const { data: usersPage } = useUsers();
  const users = usersPage?.data ?? [];
  const approveRequest = useApproveRequest(Number(id) ?? 0);
  const deleteRequest = useDeleteRequest();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const createdByPicture = useUserPicture(request?.createdBy.id);
  const approvedByPicture = useUserPicture(request?.approvedBy?.id ?? undefined);
  const supervisorPicture = useUserPicture(request?.supervisor?.id ?? undefined);

  if (isLoading) return <PageLoader />;
  if (!request) return <p className={styles.emptyText}>{t("common.noData")}</p>;

  const isAuthor = currentUser?.id === request.createdBy.id;
  const isAdmin = currentUser?.isAdmin === true;
  const isSupervisor = currentUser !== undefined && request.supervisor?.id === currentUser.id;
  const canEdit = (isAuthor && request.status === "draft") || isAdmin;
  const canDelete = (isAuthor && request.status === "draft") || isAdmin;
  const canApprove = isSupervisor && request.status === "draft";
  const approvedByUser = request.approvedBy ? (users.find((user) => user.id === request.approvedBy?.id) ?? null) : null;

  const responsibleDepartmentLabels: Record<NonNullable<DevelopmentRequestResponse["responsibleDepartment"]>, string> = {
    integrations: t("requests.responsibleDepartmentIntegrations"),
    software_development: t("requests.responsibleDepartmentSoftwareDevelopment"),
    ict: t("requests.responsibleDepartmentIct"),
  };

  return (
    <Page width="wide">
      <Card>
        <div className={styles.cardHeaderActions}>
          <div>
            <BackButton label={t("requests.backToRequests")} onClick={() => navigate("/requests")} />
          </div>
          <Badge variant={request.status} label={statusLabel(request.status, t)} />
          {canApprove && (
            <Button variant="primary" onClick={() => approveRequest.mutate()} loading={approveRequest.isPending}>
              {t("requests.approve")}
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" onClick={() => navigate(`/requests/${id}/edit`)}>
              {t("common.edit")}
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setDeleteModalOpen(true)}>
              {t("common.delete")}
            </Button>
          )}
        </div>

        <div className={styles.divider} />

        {/* Description */}
        <div className={styles.descSection}>
          <span className={styles.sectionLabel}>{t("requests.description")}</span>
          <p className={styles.descText}>{request.description}</p>
        </div>

        {/* Stats grid */}
        <div className={styles.statsGrid}>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("requests.estimateDays")}</span>
            {request.estimateDays !== null ? (
              <span className={styles.statTileValue}>{request.estimateDays}</span>
            ) : (
              <span className={styles.statTileMuted}>{t("common.notSet")}</span>
            )}
          </div>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("requests.audience")}</span>
            <Badge
              variant={request.audience}
              label={request.audience === "internal" ? t("requests.audienceInternal") : t("requests.audienceExternal")}
            />
          </div>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("requests.supervisor")}</span>
            {request.supervisor ? (
              <div className={styles.statTileUser}>
                <Avatar user={request.supervisor} pictureUrl={supervisorPicture.data} />
                <span className={styles.statTileUserName}>{formatUserName(request.supervisor)}</span>
              </div>
            ) : (
              <span className={styles.statTileMuted}>{t("common.notSet")}</span>
            )}
          </div>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("requests.needsErpIntegration")}</span>
            <div className={styles.statTileRow}>
              <span className={`${styles.dot} ${request.needsErpIntegration ? styles.dotOn : styles.dotOff}`} />
              <span className={styles.statTileMuted}>
                {request.needsErpIntegration ? t("common.yes") : t("common.no")}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        {/* Responsible department (admin only) */}
        {isAdmin && (
          <div className={styles.inlineRow}>
            <div className={styles.inlineGroup}>
              <span className={styles.inlineLabel}>{t("requests.responsibleDepartment")}</span>
              {request.responsibleDepartment ? (
                <span className={styles.inlineValue}>{responsibleDepartmentLabels[request.responsibleDepartment]}</span>
              ) : (
                <span className={styles.inlineMuted}>{t("common.notSet")}</span>
              )}
            </div>
          </div>
        )}

        {/* Departments */}
        <div className={`${styles.inlineRow} ${styles.inlineRowBorder}`}>
          <div className={styles.inlineGroup}>
            <span className={styles.inlineLabel}>{t("requests.departments")}</span>
            {request.departments.length > 0 ? (
              <div className={styles.chipList}>
                {request.departments.map((dept) => (
                  <span key={dept} className={styles.chip}>{dept}</span>
                ))}
              </div>
            ) : (
              <span className={styles.inlineMuted}>{t("common.notSet")}</span>
            )}
          </div>
        </div>

        {/* Participants */}
        <div className={`${styles.inlineRow} ${styles.inlineRowBorder}`}>
          <div className={styles.inlineGroup}>
            <span className={styles.inlineLabel}>{t("requests.participants")}</span>
            {request.participants.length > 0 ? (
              <div className={styles.chipList}>
                {request.participants.map((participant) => (
                  <span key={participant.id} className={styles.chip}>
                    {formatUserName(participant)}
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.inlineMuted}>{t("common.notSet")}</span>
            )}
          </div>
        </div>

        {/* Admin: internal notes */}
        {isAdmin && request.internalNotes && (
          <div className={classNames(styles.descSection, styles.inlineRowBorder)}>
            <span className={styles.sectionLabel}>{t("requests.internalNotes")}</span>
            <p className={styles.descText}>{request.internalNotes}</p>
          </div>
        )}

        <div className={styles.divider} />

        {/* Footer: created by + date + supervisor */}
        <div className={styles.footer}>
          <div className={styles.footerCreator}>
            <Avatar user={request.createdBy} pictureUrl={createdByPicture.data} />
            <div className={styles.footerMeta}>
              <span className={styles.footerMetaLabel}>{t("requests.createdBy")}</span>
              <span className={styles.footerMetaValue}>{formatUserName(request.createdBy)}</span>
            </div>
          </div>
          <div className={styles.footerDate}>
            <span className={styles.footerMetaLabel}>{t("requests.createdAt")}</span>
            <span className={styles.footerMetaValue}>{formatDate(request.createdAt)}</span>
          </div>
        </div>

        {/* Approved footer */}
        {request.approvedAt && (
          <>
            <div className={styles.divider} />
            <div className={styles.footer}>
              <div className={styles.footerCreator}>
                {request.approvedBy && approvedByUser && (
                  <>
                    <Avatar user={approvedByUser} pictureUrl={approvedByPicture.data} />
                    <div className={styles.footerMeta}>
                      <span className={styles.footerMetaLabel}>{t("requests.approvedBy")}</span>
                      <span className={styles.footerMetaValue}>{formatUserName(request.approvedBy)}</span>
                    </div>
                  </>
                )}
              </div>
              <div className={styles.footerDate}>
                <span className={styles.footerMetaLabel}>{t("requests.approvedAt")}</span>
                <span className={styles.footerMetaValue}>{formatDate(request.approvedAt)}</span>
              </div>
            </div>
          </>
        )}
      </Card>

      <Modal
        open={deleteModalOpen}
        title={t("requests.deleteRequest")}
        onClose={() => setDeleteModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={deleteRequest.isPending}
              onClick={() => {
                deleteRequest.mutate(Number(id), {
                  onSuccess: () => navigate("/requests"),
                });
              }}
            >
              {t("common.delete")}
            </Button>
          </>
        }
      >
        <p>{t("requests.confirmDeleteRequest")}</p>
      </Modal>
    </Page>
  );
}
